/**
 * Design tokens — the single source of truth for the AdminPortal design system.
 *
 * These values are the canonical reference for every component built under
 * `src/design-system/**` and every section migrated in Phase 3. They are
 * derived from:
 *   - The redesign brief, Appendix B ("Brand tokens we have today").
 *   - The values already in effect (Chakra UI v2 defaults), so that wiring
 *     them through `chakraTheme.ts` produces a pixel-identical render in
 *     Phase 1 (Foundations changes architecture, not pixels).
 *
 * IMPORTANT (Phase 1 contract): nothing here changes the rendered output yet.
 * Scales that the current app does not visibly use (full gray/teal ramps,
 * shadow elevations, motion) are documented here as the target system and are
 * applied incrementally during the Phase 3 migrations. Values that WOULD change
 * the current render if flipped (e.g. the Inter font family from the brief) are
 * called out with `// TODO: Phase 2` and intentionally left at today's value.
 *
 * No magic numbers in components — import from here instead.
 */

/* -------------------------------------------------------------------------- */
/* Color                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Neutral gray ramp. Values match Chakra v2's default `gray` scale so that
 * referencing them is render-neutral today.
 */
export const GRAY = {
  50: "#F7FAFC",
  100: "#EDF2F7",
  200: "#E2E8F0",
  300: "#CBD5E0",
  400: "#A0AEC0",
  500: "#718096",
  600: "#4A5568",
  700: "#2D3748",
  800: "#1A202C",
  900: "#171923",
} as const;

/**
 * Brand teal ramp. `500` (#319795) is the existing YourAbility brand teal
 * (== Chakra `teal.500`); the brief says "carry the teal accent through".
 * Matching Chakra's `teal` ramp keeps this render-neutral until adopted.
 */
export const TEAL = {
  50: "#E6FFFA",
  100: "#B2F5EA",
  200: "#81E6D9",
  300: "#4FD1C5",
  400: "#38B2AC",
  500: "#319795",
  600: "#2C7A7B",
  700: "#285E61",
  800: "#234E52",
  900: "#1D4044",
} as const;

/** Semantic accent hues (success/warning/error/info). Match Chakra defaults. */
export const GREEN = { 50: "#F0FFF4", 500: "#38A169", 600: "#2F855A" } as const;
export const ORANGE = {
  50: "#FFFAF0",
  500: "#DD6B20",
  600: "#C05621",
} as const;
export const RED = { 50: "#FFF5F5", 500: "#E53E3E", 600: "#C53030" } as const;
export const BLUE = { 50: "#EBF8FF", 500: "#3182CE", 600: "#2B6CB0" } as const;
export const PURPLE = {
  50: "#FAF5FF",
  500: "#805AD5",
  600: "#6B46C1",
} as const;
export const PINK = { 50: "#FFF5F7", 500: "#D53F8C", 600: "#B83280" } as const;

/**
 * Background layers. `page` is the app canvas behind every surface; it matches
 * the current global `html, body` background exactly (rgb(241, 245, 247)).
 */
export const BACKGROUND = {
  page: "rgb(241, 245, 247)",
  surface: "#FFFFFF",
  overlay: "rgba(0, 0, 0, 0.48)",
} as const;

/**
 * Legacy brand accents preserved verbatim from the pre-redesign theme so that
 * any code reading `theme.colors.brand.*` keeps working unchanged.
 */
export const BRAND_LEGACY = {
  panelBg: "#E4ECFC",
  purpleGradient: "linear(136.13deg, #4B21D6 -93.1%, #9536A6 125.01%)",
  yellowGradient: "linear-gradient(109.5deg, #EFA537 -138.01%, #F8DB68 109.9%)",
  goldGradient: "linear-gradient(99.36deg, #EFA537 -39.65%, #F8DB68 71.68%)",
} as const;

export const COLOR = {
  gray: GRAY,
  teal: TEAL,
  green: GREEN,
  orange: ORANGE,
  red: RED,
  blue: BLUE,
  purple: PURPLE,
  pink: PINK,
  background: BACKGROUND,
} as const;

/**
 * Discipline badge palette (mockup `primitives/badges.html`). Each discipline
 * maps to a `{ subtle, fg }` pair (background + text) using Chakra color-scale
 * names so badges can also be expressed as `colorScheme`. `dotColor` is the
 * solid swatch used in the editor's discipline picker.
 */
export const DISCIPLINE_PALETTE = {
  speech: { scheme: "purple", subtle: PURPLE[50], fg: PURPLE[600] },
  aba: { scheme: "blue", subtle: BLUE[50], fg: BLUE[600] },
  ot: { scheme: "orange", subtle: ORANGE[50], fg: ORANGE[600] },
  pt: { scheme: "pink", subtle: PINK[50], fg: PINK[600] },
  spec: { scheme: "teal", subtle: TEAL[50], fg: TEAL[700] },
  parent: { scheme: "green", subtle: GREEN[50], fg: GREEN[600] },
} as const;

/**
 * Billing-rail palette — which rail bills a subscription (Monobank, Apple, Play).
 *
 * Every pair below is a **computed** AA pass on normal-size text, which the
 * obvious choices are not: `ORANGE[600]` on `ORANGE[50]` measures 4.39:1 and
 * `GREEN[600]` on `GREEN[50]` the same, so Google Play uses the blue pair
 * (5.01:1) instead. Monobank teal is 7.00:1 and Apple gray 14.49:1.
 */
export const BILLING_RAIL_PALETTE = {
  monobank: { scheme: "teal", subtle: TEAL[50], fg: TEAL[700] },
  apple_app_store: { scheme: "gray", subtle: GRAY[100], fg: GRAY[800] },
  google_play: { scheme: "blue", subtle: BLUE[50], fg: BLUE[600] },
  unknown: { scheme: "gray", subtle: GRAY[100], fg: GRAY[600] },
} as const;

/** Listing status palette (mockup `primitives/badges.html`). */
export const STATUS_PALETTE = {
  live: { scheme: "green", subtle: GREEN[50], fg: GREEN[600] },
  draft: { scheme: "gray", subtle: GRAY[100], fg: GRAY[600] },
  takenDown: { scheme: "red", subtle: RED[50], fg: RED[600] },
  pending: { scheme: "orange", subtle: ORANGE[50], fg: ORANGE[600] },
} as const;

/* -------------------------------------------------------------------------- */
/* Spacing — 4px grid (matches Chakra's space scale)                          */
/* -------------------------------------------------------------------------- */

/** Spacing scale in rem, on a 4px grid. `4` == 1rem == 16px, etc. */
export const SPACING = {
  0: "0",
  1: "0.25rem", // 4px
  2: "0.5rem", // 8px
  3: "0.75rem", // 12px
  4: "1rem", // 16px
  5: "1.25rem", // 20px
  6: "1.5rem", // 24px
  8: "2rem", // 32px
  10: "2.5rem", // 40px
  12: "3rem", // 48px
  16: "4rem", // 64px
  20: "5rem", // 80px
  24: "6rem", // 96px
} as const;

/* -------------------------------------------------------------------------- */
/* Typography                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Font families.
 *
 * Inter is the design-system family (mockup `foundations/tokens.html`), verified
 * to cover Cyrillic ("Лексика Cyrillic OK" in the mockup type specimen). The
 * webfont is loaded in `public/index.html`; `'Segoe UI', system-ui, sans-serif`
 * is the fallback chain while it loads or on environments without Inter.
 */
export const FONT_FAMILY = {
  heading: "'Inter', 'Segoe UI', system-ui, sans-serif",
  body: "'Inter', 'Segoe UI', system-ui, sans-serif",
  mono: "'SF Mono', Menlo, Monaco, Consolas, monospace",
} as const;

/** Type scale (matches Chakra's default `fontSizes`). */
export const FONT_SIZE = {
  xs: "0.75rem", // 12px
  sm: "0.875rem", // 14px
  md: "1rem", // 16px
  lg: "1.125rem", // 18px
  xl: "1.25rem", // 20px
  "2xl": "1.5rem", // 24px
  "3xl": "1.875rem", // 30px
} as const;

export const FONT_WEIGHT = {
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
} as const;

export const LINE_HEIGHT = {
  none: 1,
  tight: 1.25,
  base: 1.5,
  relaxed: 1.75,
} as const;

/* -------------------------------------------------------------------------- */
/* Radii                                                                      */
/* -------------------------------------------------------------------------- */

/** Corner radii. Brief Appendix B target ramp: 4 / 6 / 8 / 12 / full. */
export const RADII = {
  sm: "0.25rem", // 4px
  md: "0.375rem", // 6px
  lg: "0.5rem", // 8px
  xl: "0.75rem", // 12px
  full: "9999px",
} as const;

/* -------------------------------------------------------------------------- */
/* Shadows — 4 elevation levels                                               */
/* -------------------------------------------------------------------------- */

/**
 * Surface elevation — exact values from the mockup `foundations/tokens.html`
 * (`--shadow-1..4`). Mapped to the four levels:
 *   1 cards · 2 side panels · 3 modals · 4 toasts / command palette.
 */
export const SHADOW = {
  card: "0 1px 2px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.08)",
  panel: "0 4px 6px rgba(0, 0, 0, 0.07), 0 1px 3px rgba(0, 0, 0, 0.06)",
  modal: "0 10px 15px rgba(0, 0, 0, 0.08), 0 4px 6px rgba(0, 0, 0, 0.05)",
  toast: "0 20px 25px rgba(0, 0, 0, 0.1), 0 10px 10px rgba(0, 0, 0, 0.04)",
} as const;

/* -------------------------------------------------------------------------- */
/* Motion                                                                     */
/* -------------------------------------------------------------------------- */

/** Durations (ms) and easing curves. Kept subtle per the brief. */
export const MOTION = {
  duration: {
    instant: "0ms",
    fast: "150ms",
    normal: "200ms",
    slow: "300ms",
  },
  easing: {
    standard: "cubic-bezier(0.4, 0, 0.2, 1)",
    accelerate: "cubic-bezier(0.4, 0, 1, 1)",
    decelerate: "cubic-bezier(0, 0, 0.2, 1)",
  },
} as const;

/* -------------------------------------------------------------------------- */
/* Aggregate                                                                  */
/* -------------------------------------------------------------------------- */

export const tokens = {
  color: COLOR,
  brandLegacy: BRAND_LEGACY,
  discipline: DISCIPLINE_PALETTE,
  status: STATUS_PALETTE,
  billingRail: BILLING_RAIL_PALETTE,
  spacing: SPACING,
  fontFamily: FONT_FAMILY,
  fontSize: FONT_SIZE,
  fontWeight: FONT_WEIGHT,
  lineHeight: LINE_HEIGHT,
  radii: RADII,
  shadow: SHADOW,
  motion: MOTION,
} as const;

export type Tokens = typeof tokens;

export default tokens;
